import { z } from "zod";

/** Referral code format shared between client, tracker, and server insert policy. */
export const REFERRAL_CODE_REGEX = /^[A-Z0-9-]{4,32}$/;

export const referralCodeSchema = z
  .string()
  .trim()
  .toUpperCase()
  .regex(REFERRAL_CODE_REGEX, "Invalid referral code");

export const PROMO_CHANNELS = [
  "facebook",
  "instagram",
  "x_twitter",
  "tiktok",
  "youtube",
  "email",
  "friends",
  "blog",
  "forum",
  "other",
] as const;

export const PAYOUT_METHODS = ["bank", "paypal", "wise", "revolut"] as const;

// Loose international phone: leading +, 6–20 digits (spaces / dashes tolerated but trimmed).
const phoneSchema = z
  .string()
  .trim()
  .min(6, "Enter a valid phone number")
  .max(24, "Phone number is too long")
  .regex(/^\+?[0-9 ()-]{6,24}$/, "Enter a valid phone number");

export const applyForReferralSchema = z
  .object({
    first_name: z.string().trim().min(2, "First name is required").max(60),
    last_name: z.string().trim().min(2, "Last name is required").max(60),
    phone: phoneSchema,
    country: z.string().trim().length(2).toUpperCase(),
    languages: z
      .array(z.string().trim().min(2).max(8))
      .min(1, "Pick at least one language")
      .max(12),
    promo_channels: z
      .array(z.enum(PROMO_CHANNELS))
      .min(1, "Pick at least one channel")
      .max(PROMO_CHANNELS.length),
    pitch: z.string().trim().max(500).optional().or(z.literal("")),
    payout_method: z.enum(PAYOUT_METHODS),
    payout_details: z.object({
      account_name: z.string().trim().max(120).optional(),
      iban: z.string().trim().max(64).optional(),
      swift: z.string().trim().max(16).optional(),
      paypal_email: z.string().trim().email().max(200).optional().or(z.literal("")),
      wise_email: z.string().trim().email().max(200).optional().or(z.literal("")),
      revolut_tag: z.string().trim().max(64).optional(),
    }),
    accepted_terms_version: z.number().int().min(1),
  })
  .transform((v) => ({
    ...v,
    display_name: `${v.first_name} ${v.last_name}`.trim(),
  }));

export type ApplyForReferralInput = z.input<typeof applyForReferralSchema>;
export type ApplyForReferralOutput = z.output<typeof applyForReferralSchema>;
